import { Timestamp } from "firebase/firestore";
import { z } from "zod";

/**
 * `users/{uid}/connections/{otherUid}` (TNG-93). Written only by the
 * connection request trigger; a snapshot of the other person's card as it was
 * when they last met, with one tag per event they met in.
 */
export const connectionEventSchema = z.object({
  title: z.string(),
  slug: z.string(),
  /** The other person's roster row there: what the private note is keyed by. */
  attendeeId: z.string(),
  connectedAt: z.instanceof(Timestamp),
});
export type ConnectionEvent = z.infer<typeof connectionEventSchema>;

export const connectionSchema = z.object({
  otherUid: z.string(),
  name: z.string(),
  role: z.string().optional(),
  company: z.string().optional(),
  avatar: z.string().optional(),
  linkedinUrl: z.string().optional(),
  email: z.string().optional(),
  events: z.record(z.string(), connectionEventSchema),
  firstConnectedAt: z.instanceof(Timestamp),
  updatedAt: z.instanceof(Timestamp),
});
export type Connection = z.infer<typeof connectionSchema>;

export const CONNECTION_REQUEST_TYPES = [
  "ISSUE_CONNECT_CODE",
  "ROTATE_CONNECT_CODE",
  "CONNECT",
  "REMOVE_CONNECTION",
] as const;
export type ConnectionRequestType = (typeof CONNECTION_REQUEST_TYPES)[number];

/** `connection_requests/{id}`, as its requester reads it back. */
export const connectionRequestSchema = z.object({
  type: z.enum(CONNECTION_REQUEST_TYPES),
  requestedBy: z.string(),
  status: z.enum(["REQUESTED", "IN_PROGRESS", "SUCCEEDED", "FAILED"]),
  code: z.string().optional(),
  eventId: z.string().optional(),
  otherUid: z.string().optional(),
  error: z.string().optional(),
  targetUid: z.string().optional(),
  targetName: z.string().optional(),
});
export type ConnectionRequest = z.infer<typeof connectionRequestSchema>;

/** The event tags of a connection, most recent meeting first. */
export function eventsNewestFirst(
  connection: Pick<Connection, "events">,
): (ConnectionEvent & { eventId: string })[] {
  return Object.entries(connection.events)
    .map(([eventId, tag]) => ({ ...tag, eventId }))
    .sort((a, b) => b.connectedAt.toMillis() - a.connectedAt.toMillis());
}
